import { css } from "styled-system/css";
import { useCallback, useEffect, useState } from "react";
import { AdminPageShell } from "@/components/admin/admin-page-shell";
import { useAuth } from "@/providers/AuthProvider";
import { fetchWithSessionSync } from "@/lib/fetch-with-session";
import { Badge, type BadgeTone } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { LoadingFrame } from "@/components/ui/loading-frame";
import { EmptyState } from "@/components/admin/empty-state";
import { useToast } from "@/hooks/useToast";

type DiscountCodeStatus = "activo" | "agotado" | "desactivado";

type DiscountCode = {
  id: string;
  code: string;
  percentOff: number;
  durationDays: number | null;
  maxUses: number;
  usedCount: number;
  isActive: boolean;
  status: DiscountCodeStatus;
  planSlugs: string[];
  planIds: string[];
  createdAt: string;
};

type PlanOption = {
  id: string;
  slug: string;
  name: string;
  kind: string;
  frequency: string;
  amount: number;
  currency: string;
  isActive: boolean;
};

type FormState = {
  code: string;
  percentOff: string;
  durationDays: string;
  maxUses: string;
  planIds: string[];
  isActive: boolean;
};

const EMPTY_FORM: FormState = {
  code: "",
  percentOff: "",
  durationDays: "",
  maxUses: "",
  planIds: [],
  isActive: true,
};

const STATUS_TONE: Record<DiscountCodeStatus, BadgeTone> = {
  activo: "success",
  agotado: "warning",
  desactivado: "neutral",
};

const STATUS_LABEL: Record<DiscountCodeStatus, string> = {
  activo: "Activo",
  agotado: "Agotado",
  desactivado: "Desactivado",
};

async function parseErrorMessage(r: Response, fallback: string): Promise<string> {
  const j = (await r.json().catch(() => ({}))) as { error?: string };
  if (j.error && j.error.length < 160 && !/[{}[\]]/.test(j.error)) return j.error;
  return fallback;
}

export function AdminDiscountCodes() {
  const { refresh } = useAuth();
  const toast = useToast();
  const [codes, setCodes] = useState<DiscountCode[] | null>(null);
  const [plans, setPlans] = useState<PlanOption[] | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [codesRes, plansRes] = await Promise.all([
      fetchWithSessionSync("/api/billing/discount-codes", { credentials: "include" }, refresh),
      fetchWithSessionSync("/api/billing/plans", { credentials: "include" }, refresh),
    ]);
    if (codesRes.ok) setCodes((await codesRes.json()) as DiscountCode[]);
    if (plansRes.ok) setPlans((await plansRes.json()) as PlanOption[]);
  }, [refresh]);

  useEffect(() => {
    void load();
  }, [load]);

  function startEdit(c: DiscountCode) {
    setEditingId(c.id);
    setForm({
      code: c.code,
      percentOff: String(c.percentOff),
      durationDays: c.durationDays != null ? String(c.durationDays) : "",
      maxUses: String(c.maxUses),
      planIds: c.planIds,
      isActive: c.isActive,
    });
  }

  function cancelEdit() {
    setEditingId(null);
    setForm(EMPTY_FORM);
  }

  function togglePlan(planId: string) {
    setForm((prev) => ({
      ...prev,
      planIds: prev.planIds.includes(planId)
        ? prev.planIds.filter((id) => id !== planId)
        : [...prev.planIds, planId],
    }));
  }

  async function submit() {
    const percentOff = Number(form.percentOff);
    const maxUses = Number(form.maxUses);
    const durationDays = form.durationDays.trim() ? Number(form.durationDays) : undefined;

    if (!editingId && !form.code.trim()) {
      toast.error("Ingresá un código");
      return;
    }
    if (!Number.isFinite(percentOff) || percentOff <= 0 || percentOff > 100) {
      toast.error("El porcentaje debe estar entre 1 y 100");
      return;
    }
    if (!Number.isInteger(maxUses) || maxUses <= 0) {
      toast.error("La cantidad máxima de usos debe ser un entero positivo");
      return;
    }
    if (form.planIds.length === 0) {
      toast.error("Seleccioná al menos un plan aplicable");
      return;
    }

    setBusy(true);
    const r = editingId
      ? await fetchWithSessionSync(
          `/api/billing/discount-codes/${editingId}`,
          {
            method: "PUT",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              percentOff,
              durationDays: durationDays ?? 0,
              maxUses,
              planIds: form.planIds,
              isActive: form.isActive,
            }),
          },
          refresh,
        )
      : await fetchWithSessionSync(
          "/api/billing/discount-codes",
          {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              code: form.code,
              percentOff,
              durationDays: durationDays ?? 0,
              maxUses,
              planIds: form.planIds,
            }),
          },
          refresh,
        );
    setBusy(false);

    if (!r.ok) {
      toast.error(await parseErrorMessage(r, "No pudimos guardar el código."));
      return;
    }
    toast.success(editingId ? "Código actualizado." : "Código creado.");
    cancelEdit();
    void load();
  }

  if (!codes || !plans) {
    return (
      <AdminPageShell title="Códigos de descuento">
        <LoadingFrame variant="light" minHeight={320} />
      </AdminPageShell>
    );
  }

  return (
    <AdminPageShell
      title="Códigos de descuento"
      description="Creá y gestioná códigos de descuento para las suscripciones de Pro/Enterprise."
    >
      <div className={css({ display: "flex", flexDir: "column", gap: "6" })}>
        {/* Form crear/editar */}
        <div className={css({ p: "6", rounded: "xl", borderWidth: "1px", borderColor: "border.DEFAULT", bg: "bg.card" })}>
          <p className={css({ fontWeight: "semibold", color: "fg.DEFAULT", mb: "4" })}>
            {editingId ? `Editando "${form.code}"` : "Nuevo código"}
          </p>

          <div className={css({ display: "grid", gridTemplateColumns: { base: "1fr", md: "repeat(4, 1fr)" }, gap: "4" })}>
            <label className={css({ display: "flex", flexDir: "column", gap: "1", fontSize: "sm" })}>
              <span className={css({ fontWeight: "medium", color: "fg.muted" })}>Código</span>
              <input
                value={form.code}
                disabled={editingId != null}
                onChange={(e) => setForm((p) => ({ ...p, code: e.target.value }))}
                placeholder="PROMO70"
                className={css({
                  h: "9",
                  px: "3",
                  rounded: "md",
                  borderWidth: "1px",
                  borderColor: "border.DEFAULT",
                  bg: editingId != null ? "bg.muted" : "white",
                  fontSize: "sm",
                })}
              />
            </label>

            <label className={css({ display: "flex", flexDir: "column", gap: "1", fontSize: "sm" })}>
              <span className={css({ fontWeight: "medium", color: "fg.muted" })}>% descuento</span>
              <input
                type="number"
                min={1}
                max={100}
                value={form.percentOff}
                onChange={(e) => setForm((p) => ({ ...p, percentOff: e.target.value }))}
                placeholder="70"
                className={css({ h: "9", px: "3", rounded: "md", borderWidth: "1px", borderColor: "border.DEFAULT", bg: "white", fontSize: "sm" })}
              />
            </label>

            <label className={css({ display: "flex", flexDir: "column", gap: "1", fontSize: "sm" })}>
              <span className={css({ fontWeight: "medium", color: "fg.muted" })}>Duración (días)</span>
              <input
                type="number"
                min={0}
                value={form.durationDays}
                onChange={(e) => setForm((p) => ({ ...p, durationDays: e.target.value }))}
                placeholder="Vacío = para siempre"
                className={css({ h: "9", px: "3", rounded: "md", borderWidth: "1px", borderColor: "border.DEFAULT", bg: "white", fontSize: "sm" })}
              />
            </label>

            <label className={css({ display: "flex", flexDir: "column", gap: "1", fontSize: "sm" })}>
              <span className={css({ fontWeight: "medium", color: "fg.muted" })}>Máx. usos</span>
              <input
                type="number"
                min={1}
                value={form.maxUses}
                onChange={(e) => setForm((p) => ({ ...p, maxUses: e.target.value }))}
                placeholder="50"
                className={css({ h: "9", px: "3", rounded: "md", borderWidth: "1px", borderColor: "border.DEFAULT", bg: "white", fontSize: "sm" })}
              />
            </label>
          </div>

          <div className={css({ mt: "4" })}>
            <span className={css({ fontSize: "sm", fontWeight: "medium", color: "fg.muted" })}>Planes aplicables</span>
            <div className={css({ display: "flex", flexWrap: "wrap", gap: "3", mt: "2" })}>
              {plans.map((p) => (
                <label
                  key={p.id}
                  className={css({
                    display: "flex",
                    alignItems: "center",
                    gap: "2",
                    fontSize: "sm",
                    px: "3",
                    py: "1.5",
                    rounded: "md",
                    borderWidth: "1px",
                    borderColor: form.planIds.includes(p.id) ? "brand.500" : "border.DEFAULT",
                    bg: form.planIds.includes(p.id) ? "brand.50" : "white",
                    cursor: "pointer",
                  })}
                >
                  <input
                    type="checkbox"
                    checked={form.planIds.includes(p.id)}
                    onChange={() => togglePlan(p.id)}
                  />
                  {p.name} <span className={css({ color: "fg.muted", fontSize: "xs" })}>({p.slug})</span>
                </label>
              ))}
            </div>
          </div>

          {editingId ? (
            <label className={css({ display: "flex", alignItems: "center", gap: "2", fontSize: "sm", mt: "4" })}>
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => setForm((p) => ({ ...p, isActive: e.target.checked }))}
              />
              Activo
            </label>
          ) : null}

          <div className={css({ display: "flex", gap: "3", mt: "5" })}>
            <Button onClick={() => void submit()} disabled={busy}>
              {busy ? "Guardando…" : editingId ? "Guardar cambios" : "Crear código"}
            </Button>
            {editingId ? (
              <Button variant="secondary" onClick={cancelEdit} disabled={busy}>
                Cancelar
              </Button>
            ) : null}
          </div>
        </div>

        {/* Listado */}
        {codes.length === 0 ? (
          <EmptyState
            title="Sin códigos todavía"
            description="Creá el primero desde el formulario de arriba."
            icon="🏷️"
          />
        ) : (
          <div className={css({ overflowX: "auto", rounded: "xl", borderWidth: "1px", borderColor: "border.DEFAULT", bg: "bg.card" })}>
            <table className={css({ w: "full", borderCollapse: "collapse", fontSize: "sm" })}>
              <thead>
                <tr className={css({ bg: "bg.muted", textAlign: "left" })}>
                  <th className={css({ py: "3", px: "4", fontWeight: "semibold", color: "fg.muted" })}>Código</th>
                  <th className={css({ py: "3", px: "4", fontWeight: "semibold", color: "fg.muted" })}>%</th>
                  <th className={css({ py: "3", px: "4", fontWeight: "semibold", color: "fg.muted" })}>Duración</th>
                  <th className={css({ py: "3", px: "4", fontWeight: "semibold", color: "fg.muted" })}>Planes</th>
                  <th className={css({ py: "3", px: "4", fontWeight: "semibold", color: "fg.muted" })}>Usos</th>
                  <th className={css({ py: "3", px: "4", fontWeight: "semibold", color: "fg.muted" })}>Estado</th>
                  <th className={css({ py: "3", px: "4" })} />
                </tr>
              </thead>
              <tbody>
                {codes.map((c) => (
                  <tr key={c.id} className={css({ borderTopWidth: "1px", borderColor: "border.DEFAULT", _hover: { bg: "bg.muted" } })}>
                    <td className={css({ py: "3", px: "4", fontWeight: "semibold" })}>{c.code}</td>
                    <td className={css({ py: "3", px: "4" })}>{c.percentOff}%</td>
                    <td className={css({ py: "3", px: "4" })}>{c.durationDays ? `${c.durationDays} días` : "Para siempre"}</td>
                    <td className={css({ py: "3", px: "4", fontSize: "xs", color: "fg.muted" })}>{c.planSlugs.join(", ")}</td>
                    <td className={css({ py: "3", px: "4" })}>{c.usedCount} / {c.maxUses}</td>
                    <td className={css({ py: "3", px: "4" })}>
                      <Badge tone={STATUS_TONE[c.status]}>{STATUS_LABEL[c.status]}</Badge>
                    </td>
                    <td className={css({ py: "3", px: "4", textAlign: "right" })}>
                      <Button variant="secondary" size="sm" onClick={() => startEdit(c)}>
                        Editar
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </AdminPageShell>
  );
}
